import React from 'react';

import ErrorIcon from '@mui/icons-material/ErrorOutline';
// material-ui
import { InputAdornment, TextField } from '@mui/material';

import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';

// components
import InputLabel from './inputLabel';

interface StyledProps {
    removeBorder?: boolean;
    overrideInputStyles?: React.CSSProperties;
    overrideInputRootStyles?: React.CSSProperties;
    overrideDisabledInputStyles?: React.CSSProperties;
}

export interface TextInputProps {
    id?: string;
    label?: string;
    placeholder?: string;
    leadingIcon?: any;
    trailingIcon?: any;
    trailingIconAction?: (e: any) => void;
    name?: string;
    value?: string | number;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    isRequired?: boolean;
    type?: 'text' | 'number' | 'password';
    description?: string;
    error?: string;
    touched?: boolean;
    inputStyle?: React.CSSProperties;
    overrideInputStyles?: React.CSSProperties;
    overrideInputRootStyles?: React.CSSProperties;
    overrideDisabledInputStyles?: React.CSSProperties;
    wrapperStyle?: React.CSSProperties;
    isDisabled?: boolean;
    inputRef?: React.Ref<any>;
    isMultiline?: boolean;
    removeBorder?: boolean;
    className?: string;
    leadingIconAction?: (e: any) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    autoFocus?: boolean;
    min?: number;
    max?: number;
    maxLength?: number;
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    rows?: number;
}

const TextInput: React.FC<React.PropsWithChildren<TextInputProps>> = ({
    id,
    label,
    placeholder,
    leadingIcon,
    trailingIcon,
    trailingIconAction = () => null,
    name,
    value = '',
    onChange,
    onBlur,
    isRequired,
    type = 'text',
    description,
    error,
    touched,
    inputStyle,
    wrapperStyle,
    isDisabled,
    inputRef,
    isMultiline = false,
    removeBorder,
    className,
    leadingIconAction,
    onKeyDown,
    autoFocus,
    overrideInputStyles,
    overrideDisabledInputStyles,
    overrideInputRootStyles,
    min,
    max,
    maxLength,
    onClick,
    rows,
}) => {
    const displayError = !!error && touched;
    return (
        <div style={{ ...wrapperStyle }}>
            {label && (
                <InputLabel
                    label={label}
                    isRequired={isRequired}
                    error={error}
                    touched={touched}
                    style={wrapperStyle}
                    htmlFor={id}
                />
            )}
            <AampeTextField
                id={id}
                autoFocus={autoFocus}
                className={className}
                placeholder={placeholder}
                rows={rows ? rows : 4}
                multiline={isMultiline}
                inputRef={inputRef}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                variant="outlined"
                helperText={displayError ? error : description}
                error={displayError}
                disabled={isDisabled}
                onClick={onClick}
                InputProps={{
                    startAdornment: leadingIcon && (
                        <InputAdornment position={'start'}>
                            <StyledLeadingIcon
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={leadingIconAction}
                            >
                                {leadingIcon}
                            </StyledLeadingIcon>
                        </InputAdornment>
                    ),
                    endAdornment: (displayError || trailingIcon) && (
                        <InputAdornment position={'end'}>
                            {displayError ? (
                                <AampeErrorIcon
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <StyledLeadingIcon
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={trailingIconAction}
                                >
                                    {trailingIcon}
                                </StyledLeadingIcon>
                            )}
                        </InputAdornment>
                    ),
                }}
                inputProps={{ min, max, maxLength }}
                removeBorder={removeBorder}
                style={{
                    ...inputStyle,
                }}
                overrideInputStyles={overrideInputStyles}
                overrideInputRootStyles={overrideInputRootStyles}
                overrideDisabledInputStyles={overrideDisabledInputStyles}
                onKeyDown={onKeyDown}
            />
        </div>
    );
};

export default TextInput;

export const AampeTextField = styled(TextField).withConfig({
    shouldForwardProp: (prop) =>
        ![
            'removeBorder',
            'overrideInputRootStyles',
            'overrideInputStyles',
            'overrideDisabledInputStyles',
        ].includes(prop),
})<StyledProps>(
    ({
        removeBorder,
        overrideInputRootStyles,
        overrideInputStyles,
        overrideDisabledInputStyles,
    }) => ({
        '& .MuiInputBase-root': {
            backgroundColor: aampeTheme.colors.white,
            borderRadius: aampeTheme.borderRadius.default,
            borderColor: aampeTheme.colors.bordersWG01,
            minHeight: removeBorder
                ? '38px'
                : aampeTheme.size.inputHeight.defaultV2,
            ...overrideInputRootStyles,
        },
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: aampeTheme.colors.bordersWG03,
            },
            '&.Mui-focused fieldset': {
                border: removeBorder
                    ? 'none'
                    : `1px solid ${aampeTheme.colors.solidA01}`,
            },
            '&::placeholder': {
                color: aampeTheme.colors.textAccessibleWG01,
                opacity: 0.8,
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: removeBorder
                ? aampeTheme.spacing(0.5, 2)
                : aampeTheme.spacing(1, 2),
            color: aampeTheme.colors.gray800,
            backgroundColor: removeBorder
                ? aampeTheme.colors.bordersWG01
                : aampeTheme.colors.white,
            borderRadius: aampeTheme.borderRadius.default,
            ...overrideInputStyles,
        },
        '& .MuiOutlinedInput-multiline': {
            padding: aampeTheme.spacing(1, 0),
        },
        '& .MuiFormHelperText-contained': {
            margin: aampeTheme.spacing(0.5, 0, 0),
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: aampeTheme.colors.error,
        },
        '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: aampeTheme.colors.backgroundWG01,
            ...overrideDisabledInputStyles,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: removeBorder
                ? 'none'
                : `1px solid ${aampeTheme.colors.bordersWG01}`,
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                opacity: 1,
            },
        },
    }),
);

const AampeErrorIcon = styled(ErrorIcon)({
    color: aampeTheme.colors.error500,
    fontSize: '16px',
});
const StyledLeadingIcon = styled.div({
    cursor: 'pointer',
    display: 'flex',
});
